import { ref } from 'vue';

const ALREADY_OPEN_ERROR_CODE = 12007;
const ALREADY_OPEN_RETRY_DELAY_MS = 300;
const ALREADY_OPEN_MAX_RETRIES = 5;

export interface OfficeDialogHandle {
  close(): void;
  sendMessage(message: unknown): void;
}

export interface OpenDialogOptions {
  width?: number;
  height?: number;
  fitToViewport?: boolean;
  onMessage?: (data: unknown) => void;
  onEvent?: (arg: { error: number }) => void;
}

export function useOfficeDialog() {
  const isOpening = ref(false);
  const isDialogOpen = ref(false);

  async function openDialog(
    url: string,
    options: OpenDialogOptions = {}
  ): Promise<OfficeDialogHandle | null> {
    isOpening.value = true;
    isDialogOpen.value = true;

    const width = options.fitToViewport ? 100 : (options.width ?? 80);
    const height = options.fitToViewport ? 100 : (options.height ?? 80);

    return new Promise((resolve, reject) => {
      let retriesLeft = ALREADY_OPEN_MAX_RETRIES;

      const attempt = () => {
        try {
          Office.context.ui.displayDialogAsync(
            url,
            { width, height, promptBeforeOpen: false },
            (asyncResult) => {
              isOpening.value = false;

              if (asyncResult.status !== Office.AsyncResultStatus.Failed) {
                const dialog = asyncResult.value;

                dialog.addEventHandler(
                  Office.EventType.DialogEventReceived,
                  (arg) => {
                    const eventArg = arg as { error?: number };

                    // Dialog closed by user (X button) or programmatically
                    if (
                      eventArg.error !== undefined &&
                      eventArg.error !== ALREADY_OPEN_ERROR_CODE
                    ) {
                      isDialogOpen.value = false;
                    }
                    options.onEvent?.(arg as { error: number });
                  }
                );

                dialog.addEventHandler(
                  Office.EventType.DialogMessageReceived,
                  (arg) => {
                    const msgArg = arg as { message?: string; error?: number };
                    if (msgArg.message) {
                      try {
                        options.onMessage?.(JSON.parse(msgArg.message));
                      } catch {
                        options.onMessage?.(msgArg.message);
                      }
                    }
                  }
                );

                resolve({
                  close: () => {
                    isDialogOpen.value = false;
                    dialog.close();
                  },
                  sendMessage: (message: unknown) =>
                    dialog.messageChild(JSON.stringify(message)),
                });
              } else if (asyncResult.error.code === ALREADY_OPEN_ERROR_CODE) {
                if (retriesLeft > 0) {
                  retriesLeft--;
                  setTimeout(attempt, ALREADY_OPEN_RETRY_DELAY_MS);
                } else {
                  isDialogOpen.value = false;
                  resolve(null);
                }
              } else {
                isOpening.value = false;
                isDialogOpen.value = false;
                reject(new Error(asyncResult.error.message));
              }
            }
          );
        } catch (error) {
          isOpening.value = false;
          isDialogOpen.value = false;
          reject(error);
        }
      };

      attempt();
    });
  }

  return { openDialog, isOpening, isDialogOpen };
}
